import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, X } from 'lucide-react';
import { apiClient, get, put } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Avatar } from '@/components/atoms/Avatar';
import { CharCount } from '@/components/atoms/CharCount';
import { Skeleton } from '@/components/atoms/Skeleton';
import { LIMITS } from '@/constants/limits';
import { isReservedNickname } from '@/constants/reservedNicknames';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import { cn } from '@/lib/cn';

interface MeData {
  user_id: string;
  nickname: string;
  bio: string | null;
  profile_image_url: string | null;
  interest_tags: string[] | null;
  invest_start_year: number | null;
  invest_year_domestic: number | null;
  invest_year_usa: number | null;
  invest_year_coin: number | null;
  invest_year_etc: number | null;
}

type InvestCategory = 'domestic' | 'usa' | 'coin' | 'etc';
const INVEST_CATEGORIES: Array<{ key: InvestCategory; label: string }> = [
  { key: 'domestic', label: '국내주식' },
  { key: 'usa', label: '미국주식' },
  { key: 'coin', label: '코인' },
  { key: 'etc', label: '기타' },
];

/** startYear 를 "N년차 / 10년 이상" 라벨로 변환 */
function yearLabel(startYear: number | null, currentYear: number): string | null {
  if (startYear === null) return null;
  const years = Math.max(1, currentYear - startYear + 1);
  if (years >= 10) return '10년 이상';
  return `${years}년차`;
}

const INTERESTS = [
  { id: 'coin', label: '코인' },
  { id: 'stock', label: '국내주식' },
  { id: 'global', label: '해외주식' },
  { id: 'realestate', label: '부동산' },
  { id: 'macro', label: '거시경제' },
  { id: 'ai-invest', label: 'AI·퀀트' },
];

/**
 * 투자 기간 옵션 1~10년차
 * - value: 해당 년차가 되려면 investStartYear 가 몇이어야 하는지
 * - 예) 현재 2026년 · 5년차 → startYear = 2022 (2026 - 5 + 1)
 * - 10년 이상 = 10년차로 처리 (startYear = currentYear - 9)
 */
function buildYearOptions(currentYear: number) {
  const opts: { years: number; label: string; startYear: number }[] = [];
  for (let y = 1; y <= 9; y++) {
    opts.push({ years: y, label: `${y}년차`, startYear: currentYear - y + 1 });
  }
  opts.push({ years: 10, label: '10년 이상', startYear: currentYear - 9 });
  return opts;
}

export function ProfileEditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me, setMe } = useAuthStore();

  const [nickname, setNickname] = useState('');
  const [nicknameLen, setNicknameLen] = useState(0); // IME 조합 중 포함 실제 표시 길이
  const nicknameRef = useRef<HTMLInputElement>(null);
  const overflowAlertedRef = useRef(false); // 10자 초과 얼럿 1회 표시 플래그
  const [originalNickname, setOriginalNickname] = useState('');
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  // 4개 분야별 투자 시작 년도 (null = 미선택)
  const [investYears, setInvestYears] = useState<Record<InvestCategory, number | null>>({
    domestic: null,
    usa: null,
    coin: null,
    etc: null,
  });
  // 현재 선택된 탭 (년차 그리드가 이 탭 기준으로 표시됨)
  const [activeCat, setActiveCat] = useState<InvestCategory>('domestic');
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [nickCheck, setNickCheck] = useState<{ available: boolean; reason: string | null } | null>(null);
  const [nickChecking, setNickChecking] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const currentYear = new Date().getFullYear();
  const YEAR_OPTIONS = useMemo(() => buildYearOptions(currentYear), [currentYear]);

  const meQuery = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => get<MeData>('/users/me'),
  });

  useEffect(() => {
    if (!meQuery.data || hydrated) return;
    const nk = meQuery.data.nickname ?? '';
    setNickname(nk);
    setNicknameLen(nk.length);
    setOriginalNickname(nk);
    setBio(meQuery.data.bio ?? '');
    setInterests(meQuery.data.interest_tags ?? []);
    setInvestYears({
      domestic: meQuery.data.invest_year_domestic ?? null,
      usa: meQuery.data.invest_year_usa ?? null,
      coin: meQuery.data.invest_year_coin ?? null,
      etc: meQuery.data.invest_year_etc ?? (meQuery.data.invest_start_year ?? null),
    });
    setProfileImageUrl(meQuery.data.profile_image_url ?? null);
    setHydrated(true);
  }, [meQuery.data, hydrated]);

  const handleImageUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('이미지는 최대 5MB까지 업로드할 수 있어요.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url as string | undefined;
      if (url) setProfileImageUrl(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? '업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  type FreshMe = MeData & {
    level?: number;
    score?: number;
    activity_score?: number;
    is_premium?: boolean;
    admin_role?: string | null;
    onboarding_done?: boolean;
  };

  const mutation = useMutation({
    mutationFn: () =>
      put<FreshMe>('/users/me', {
        nickname,
        bio: bio || undefined,
        interestTags: interests,
        investYearDomestic: investYears.domestic,
        investYearUsa: investYears.usa,
        investYearCoin: investYears.coin,
        investYearEtc: investYears.etc,
        profileImageUrl: profileImageUrl,
      }),
    onSuccess: (fresh) => {
      if (me && fresh) {
        setMe({
          userId: fresh.user_id,
          nickname: fresh.nickname,
          level: fresh.level ?? me.level,
          score: fresh.activity_score ?? fresh.score ?? me.score,
          isPremium: fresh.is_premium ?? me.isPremium,
          adminRole: fresh.admin_role ?? me.adminRole,
          profileImageUrl: fresh.profile_image_url ?? null,
          onboardingDone: fresh.onboarding_done ?? me.onboardingDone,
        });
      } else if (me) {
        setMe({ ...me, nickname, profileImageUrl });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      toast.success('프로필을 저장했어요.');
      navigate('/me');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '저장에 실패했어요.');
    },
  });

  const toggleInterest = (id: string) => {
    setInterests((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const runNickCheck = async () => {
    setNickChecking(true);
    try {
      const r = await get<{ available: boolean; reason: string | null }>(
        `/users/me/nickname-check?nickname=${encodeURIComponent(nickname)}`,
      );
      setNickCheck(r);
    } catch {
      setNickCheck({ available: false, reason: 'error' });
    } finally {
      setNickChecking(false);
    }
  };

  if (meQuery.isLoading || !hydrated) {
    return (
      <div className="container-base py-6">
        <Skeleton className="h-10 w-1/3 mb-6" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const NICK_RE = /^[가-힣a-zA-Z0-9_]+$/;
  const isReserved = nickname.length > 0 && isReservedNickname(nickname);
  const nicknameValid =
    nickname.length >= LIMITS.profile.nicknameMin &&
    nickname.length <= LIMITS.profile.nicknameMax &&
    NICK_RE.test(nickname) &&
    !isReserved;
  const bioValid = bio.length <= LIMITS.profile.bioMax;

  const nicknameChanged = nickname !== originalNickname;
  // 닉네임을 변경했는데 아직 중복확인을 안 했거나 불가능한 경우 저장 차단
  const nicknameCheckPassed = !nicknameChanged || (nickCheck?.available ?? false);
  const canSubmit = nicknameValid && bioValid && nicknameCheckPassed;

  return (
    <div className="container-base py-6">
      <h1 className="mb-6 text-2xl font-extrabold">프로필 수정</h1>

      {/* 프로필 이미지 */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative">
          <Avatar src={profileImageUrl} alt={nickname} size="lg" />
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-xs text-white">
              업로드 중…
            </div>
          )}
        </div>
        <div className="flex-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImageUpload(f);
            }}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Camera className="h-4 w-4" />
              {profileImageUrl ? '변경' : '이미지 업로드'}
            </Button>
            {profileImageUrl && (
              <Button size="sm" variant="ghost" onClick={() => setProfileImageUrl(null)}>
                <X className="h-4 w-4" />
                제거
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-sub)]">JPG/PNG, 최대 5MB</p>
        </div>
      </div>

      {/* 닉네임 */}
      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-medium">닉네임</label>
        <div className="flex items-center gap-2">
          <Input
            ref={nicknameRef}
            value={nickname}
            onChange={() => {
              /* onInput 에서 실제 value.length 기반으로 일괄 처리 */
            }}
            onInput={(e) => {
              // IME 조합 여부와 관계없이 항상 실제 input.value.length 로 카운트
              const raw = (e.target as HTMLInputElement).value;
              if (raw.length > LIMITS.profile.nicknameMax) {
                if (!overflowAlertedRef.current) {
                  overflowAlertedRef.current = true;
                  toast.error(`닉네임은 최대 ${LIMITS.profile.nicknameMax}글자까지 입력할 수 있어요.`);
                }
              } else {
                // 한도 이하로 내려오면 다시 얼럿 가능하도록 플래그 리셋
                overflowAlertedRef.current = false;
              }
              const val = raw.slice(0, LIMITS.profile.nicknameMax);
              setNickname(val);
              setNicknameLen(val.length);
              setNickCheck(null);
            }}
            maxLength={LIMITS.profile.nicknameMax}
            placeholder={`2~${LIMITS.profile.nicknameMax}자, 한글/영문/숫자/언더스코어`}
            error={nicknameLen > 0 && !nicknameValid}
            className="h-10 flex-1"
          />
          <Button
            size="md"
            variant="secondary"
            disabled={!nicknameValid || !nicknameChanged || nickChecking}
            loading={nickChecking}
            onClick={runNickCheck}
            className={cn(
              'h-10 shrink-0',
              // 활성화 시 초록 배경 + 흰 텍스트 (hover 에서도 유지)
              nicknameValid && nicknameChanged && !nickChecking
                ? 'border-transparent bg-[#62b058] text-white hover:bg-[#62b058] hover:text-white hover:brightness-110'
                : '',
            )}
          >
            중복 확인
          </Button>
        </div>
        <p className="mt-1 text-xs text-[var(--color-text-sub)]">
          한글 기준 최대 {LIMITS.profile.nicknameMax}글자까지 입력 가능해요.
        </p>
        <div className="mt-1 flex justify-between text-xs">
          <span
            className={
              isReserved || (nickCheck && !nickCheck.available)
                ? 'text-[var(--color-rise)]'
                : nickCheck?.available
                ? 'text-[var(--color-primary)]'
                : 'text-[var(--color-text-sub)]'
            }
          >
            {isReserved
              ? '운영 관련 용어는 닉네임으로 사용할 수 없어요.'
              : nicknameLen > 0 && !nicknameValid
              ? '한글/영문/숫자/언더스코어만 사용 가능해요.'
              : nickCheck?.available
              ? '사용 가능한 닉네임이에요!'
              : nickCheck && !nickCheck.available
              ? nickCheck.reason === 'reserved'
                ? '예약된 닉네임이에요.'
                : '이미 사용 중인 닉네임이에요.'
              : nicknameChanged
              ? '닉네임을 변경하면 중복 확인이 필요해요.'
              : ''}
          </span>
          <CharCount current={nicknameLen} max={LIMITS.profile.nicknameMax} />
        </div>
      </div>

      {/* 한줄소개 */}
      <div className="mb-6">
        <label className="mb-1.5 block text-sm font-medium">한줄소개</label>
        <Textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={LIMITS.profile.bioMax}
          rows={3}
          placeholder="자신을 간단히 소개해 보세요 (선택)"
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
        />
        <div className="mt-1 text-right">
          <CharCount current={bio.length} max={LIMITS.profile.bioMax} />
        </div>
      </div>

      {/* 관심분야 */}
      <div className="mb-6">
        <label className="mb-1.5 block text-sm font-medium">
          관심분야 <span className="text-xs text-[var(--color-text-sub)]">(최대 3개)</span>
        </label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {INTERESTS.map((i) => {
            const active = interests.includes(i.id);
            const disabled = !active && interests.length >= 3;
            return (
              <button
                key={i.id}
                type="button"
                onClick={() => toggleInterest(i.id)}
                disabled={disabled}
                className={cn(
                  'rounded-lg border p-3 text-center transition-all',
                  active
                    ? 'border-[#62b058] bg-[#62b058] text-white'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]',
                  disabled && 'opacity-40 cursor-not-allowed',
                )}
              >
                <div className="text-sm font-semibold">{i.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 투자 기간 — 탭 + 선택 요약 칩 (분야별 선택, 미선택 허용) */}
      <div className="mb-8">
        <label className="mb-1.5 block text-sm font-medium">
          투자 기간 <span className="text-xs text-[var(--color-text-sub)]">(분야별 선택, 선택 안 해도 돼요)</span>
        </label>

        {/* 선택 요약 칩 — 선택된 분야만 */}
        {INVEST_CATEGORIES.some((c) => investYears[c.key] !== null) && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {INVEST_CATEGORIES.map((cat) => {
              const sy = investYears[cat.key];
              if (sy === null) return null;
              return (
                <span
                  key={cat.key}
                  className="inline-flex items-center gap-1 rounded-full border border-[#62b058] bg-[#62b058]/10 px-2.5 py-1 text-xs font-medium text-[#4e9045]"
                >
                  {cat.label} {yearLabel(sy, currentYear)}
                  <button
                    type="button"
                    aria-label={`${cat.label} 선택 해제`}
                    onClick={() =>
                      setInvestYears((prev) => ({ ...prev, [cat.key]: null }))
                    }
                    className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[#4e9045] hover:bg-[#62b058]/20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* 분야 탭 */}
        <div
          role="tablist"
          className="flex flex-wrap gap-1 border-b border-[var(--color-border)]"
        >
          {INVEST_CATEGORIES.map((cat) => {
            const isActive = activeCat === cat.key;
            const hasValue = investYears[cat.key] !== null;
            return (
              <button
                key={cat.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCat(cat.key)}
                className={cn(
                  '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-[#62b058] text-[#4e9045]'
                    : 'border-transparent text-[var(--color-text-sub)] hover:text-[var(--color-text)]',
                )}
              >
                {cat.label}
                {hasValue && (
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      isActive ? 'bg-[#62b058]' : 'bg-[#62b058]/60',
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* 활성 탭의 년차 그리드 */}
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {YEAR_OPTIONS.map((opt) => {
            const current = investYears[activeCat];
            const active = current === opt.startYear;
            return (
              <button
                key={opt.years}
                type="button"
                onClick={() =>
                  setInvestYears((prev) => ({ ...prev, [activeCat]: opt.startYear }))
                }
                className={cn(
                  'rounded-lg border py-2 text-center text-sm font-medium transition-all',
                  active
                    ? 'border-[#62b058] bg-[#62b058] text-white'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {investYears[activeCat] !== null && (
          <button
            type="button"
            onClick={() =>
              setInvestYears((prev) => ({ ...prev, [activeCat]: null }))
            }
            className="mt-2 text-xs text-[var(--color-text-sub)] hover:underline"
          >
            이 분야 선택 해제
          </button>
        )}

        <p className="mt-3 text-xs text-[var(--color-text-sub)]">
          매년 1월 1일 기준 자동으로 1년차씩 증가해요.
        </p>
      </div>

      {/* 저장 */}
      <div className="flex gap-2">
        <Button variant="ghost" fullWidth onClick={() => navigate('/me')}>
          취소
        </Button>
        <Button
          fullWidth
          disabled={!canSubmit}
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          저장
        </Button>
      </div>
    </div>
  );
}
